import { useCallback, useEffect, useRef, useState } from 'react'

import useSourceUrlImageUpload from 'Pods/ThreeDStudio/Hooks/useSourceUrlImageUpload'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { closeImageAssistant, openImageEditor, setImageAssistantTab } from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { mockSearchImages } from '../../mockData'

export const SEARCH_STATE = { IDLE: 'idle', LOADING: 'loading', RESULTS: 'results', EMPTY: 'empty' }

/**
 * Owns the Search tab's state machine: the query, the search lifecycle
 * (idle/loading/results/empty), category drill-down, and the selected image,
 * plus the handlers wiring those to the store. Returns everything SearchTab
 * needs to render.
 */
export default function useSearchTab() {
  const [query, setQuery] = useState('')
  const [searchState, setSearchState] = useState(SEARCH_STATE.IDLE)
  const [searchResults, setSearchResults] = useState(EMPTY_ARRAY)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const uploadFromUrl = useSourceUrlImageUpload(setInputImage)

  // Guards stale mock responses (superseded search / unmount) from updating state.
  const searchIdRef = useRef(0)
  const isMountedRef = useRef(true)
  useEffect(() => () => { isMountedRef.current = false }, [])

  const runSearch = useCallback((rawTerm) => {
    const term = rawTerm.trim()
    if (!term) {
      searchIdRef.current += 1
      setSearchState(SEARCH_STATE.IDLE)
      setSearchResults(EMPTY_ARRAY)
      return
    }

    const requestId = searchIdRef.current + 1
    searchIdRef.current = requestId
    setSelectedCategory(null)
    setSelectedImage(null)
    setSearchState(SEARCH_STATE.LOADING)

    mockSearchImages(term).then((results) => {
      if (!isMountedRef.current || searchIdRef.current !== requestId) {
        return
      }
      setSearchResults(results)
      setSearchState(results.length ? SEARCH_STATE.RESULTS : SEARCH_STATE.EMPTY)
    })
  }, [])

  const handleQueryChange = useCallback((event) => {
    const { value } = event.target
    setQuery(value)
    if (!value.trim()) {
      searchIdRef.current += 1
      setSearchState(SEARCH_STATE.IDLE)
      setSearchResults(EMPTY_ARRAY)
    }
  }, [])

  const handleSearchKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      runSearch(query)
    }
  }, [query, runSearch])

  const handleSelectCategory = useCallback((category) => {
    setSelectedCategory(category)
    setSelectedImage(null)
  }, [])

  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null)
    setSelectedImage(null)
  }, [])

  // Behaves like pasting the image URL into the uploader and clicking "Upload
  // image": create a real generator asset from the URL, then close the panel.
  // Mock results use app-relative paths (/packs/...); the asset endpoint needs an
  // absolute URL it can fetch, so resolve against the current origin first.
  const handleApply = useCallback(async (image) => {
    const absoluteUrl = new URL(image.url, window.location.origin).href
    const uploaded = await uploadFromUrl(absoluteUrl)
    if (uploaded) {
      closeImageAssistant()
    }
  }, [uploadFromUrl])

  // Edit hands the image to the Edit screen and switches to it; it is not applied as
  // the input here (that happens from the Edit screen's "Use for 3D Generation").
  const handleEdit = useCallback((image) => {
    openImageEditor(image, IMAGE_ASSISTANT_TAB.SEARCH)
  }, [])

  const handleCloseExpanded = useCallback(() => setSelectedImage(null), [])

  const handleCreateImage = useCallback(() => setImageAssistantTab(IMAGE_ASSISTANT_TAB.CREATE), [])

  // Modify opens the Create tab to generate a new variation from the reference.
  const handleModify = useCallback(() => setImageAssistantTab(IMAGE_ASSISTANT_TAB.CREATE), [])

  return {
    query,
    searchState,
    searchResults,
    selectedCategory,
    selectedImage,
    onSelectImage: setSelectedImage,
    handleQueryChange,
    handleSearchKeyDown,
    handleSelectCategory,
    handleBackToCategories,
    handleApply,
    handleEdit,
    handleModify,
    handleCloseExpanded,
    handleCreateImage,
  }
}
